import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { LogOut, MessageCircle, RefreshCw } from "lucide-react";
import { Logo } from "../components/Logo";
import AdminStudents from "./AdminStudents";
import AdminTopics from "./AdminTopics";
import AdminQuestions from "./AdminQuestions";
import AdminAutoQuestions from "./AdminAutoQuestions";
import AdminPapers from "./AdminPapers";
import AdminHomework from "./AdminHomework";
import AdminTestBank from "./AdminTestBank";
import AdminReviews from "./AdminReviews";
import AdminMockTests from "./AdminMockTests";
import AdminBookings from "./AdminBookings";
import AdminCashback from "./AdminCashback";
import AdminAnalytics from "./AdminAnalytics";
import AdminTestSettings from "./AdminTestSettings";
import AdminSeries from "./AdminSeries";
import AdminProducts from "./AdminProducts";
import AdminDaily from "./AdminDaily";
import AdminOrders from "./AdminOrders";
import AdminChallenges from "./AdminChallenges";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["New Lead", "Contacted", "Demo Scheduled", "Enrolled", "Closed"];
const EXAMS = ["NEET", "JEE Main", "JEE Advanced", "CBSE Boards", "ICSE/ISC Boards", "Other"];

const formatDate = (ts) => new Date(ts * 1000).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit", hour12: true });

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [examFilter, setExamFilter] = useState("");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [notesDraft, setNotesDraft] = useState({});
  const [error, setError] = useState("");
  const [tab, setTab] = useState("leads");
  const [questionTopicFilter, setQuestionTopicFilter] = useState("");

  const manageTopicQuestions = (topicId) => {
    setQuestionTopicFilter(topicId);
    setTab("questions");
  };

  const load = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (examFilter) params.exam = examFilter;
      if (search) params.search = search;
      const [{ data: leadData }, { data: statData }] = await Promise.all([
        axios.get(`${API}/admin/leads`, { params, withCredentials: true }),
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
      ]);
      setLeads(leadData.leads);
      setStats(statData);
      setError("");
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) navigate("/admin/login");
      else setError("Could not load enquiries. Please try again.");
    }
  }, [statusFilter, examFilter, search, navigate]);

  useEffect(() => {
    if (location.state?.user) return;
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(({ data }) => setUser(data))
      .catch(() => navigate("/admin/login"));
  }, [navigate, location.state]);

  useEffect(() => {
    const timer = setTimeout(load, search ? 350 : 0);
    return () => clearTimeout(timer);
  }, [load, search]);

  const updateStatus = async (reference, status) => {
    try {
      await axios.patch(`${API}/admin/leads/${reference}`, { status }, { withCredentials: true });
      setLeads((current) => current.map((lead) => (lead.lead_reference === reference ? { ...lead, status } : lead)));
      setStats(null);
    } catch { setError("Could not update the status. Please try again."); }
  };

  const saveNotes = async (reference) => {
    try {
      await axios.patch(`${API}/admin/leads/${reference}`, { follow_up_notes: notesDraft[reference] ?? "" }, { withCredentials: true });
      setLeads((current) => current.map((lead) => (lead.lead_reference === reference ? { ...lead, follow_up_notes: notesDraft[reference] ?? "" } : lead)));
    } catch { setError("Could not save the note. Please try again."); }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    navigate("/admin/login");
  };

  const statCards = stats ? [
    ["Total enquiries", stats.total], ["New leads", stats.by_status["New Lead"] || 0],
    ["Demos scheduled", stats.by_status["Demo Scheduled"] || 0], ["Enrolled", stats.by_status["Enrolled"] || 0],
    ["Last 7 days", stats.last_7_days],
  ] : [];

  return (
    <div className="admin-shell" data-testid="admin-dashboard">
      <div className="admin-topbar"><div className="admin-topbar-inner">
        <a className="brand" href="/" data-testid="admin-brand"><Logo size={24} /><span>PRANJAL SIR <b>PHYSICS</b></span></a>
        <div className="admin-actions">
          <span className="admin-user" data-testid="admin-user-email">{user?.email || ""}</span>
          <button className="admin-btn" onClick={load} data-testid="admin-refresh"><RefreshCw size={14} /> Refresh</button>
          <button className="admin-btn" onClick={logout} data-testid="admin-logout"><LogOut size={14} /> Logout</button>
        </div>
      </div></div>
      <main className="admin-main">
        <h1>Student enquiries</h1>
        <div className="admin-tabs" data-testid="admin-tabs">{[["leads", "Leads"], ["students", "Test Students"], ["topics", "Topics"], ["ai", "AI Studio"], ["papers", "Papers"], ["mock", "Mock Tests"], ["questions", "Question Bank"], ["daily", "Daily Q"], ["challenges", "Challenges"], ["series", "Test Series"], ["products", "Pricing"], ["orders", "Orders"], ["bookings", "Bookings"], ["cashback", "Cashback"], ["analytics", "Analytics"], ["settings", "Test Settings"], ["homework", "Homework"], ["testbank", "Test Bank"], ["reviews", "Reviews"]].map(([key, label]) => <button key={key} className={tab === key ? "active" : ""} onClick={() => setTab(key)} data-testid={`tab-${key}`}>{label}</button>)}</div>
        {tab === "leads" && (<>{stats && <div className="stat-cards" data-testid="admin-stats">{statCards.map(([label, value]) => <div className="stat-card" key={label}><b>{value}</b><span>{label.toUpperCase()}</span></div>)}</div>}
        <div className="admin-filters">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, mobile or reference…" data-testid="admin-search" />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="admin-status-filter"><option value="">All statuses</option>{STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
          <select value={examFilter} onChange={(e) => setExamFilter(e.target.value)} data-testid="admin-exam-filter"><option value="">All exams</option>{EXAMS.map((exam) => <option key={exam}>{exam}</option>)}</select>
        </div>
        {error && <p className="admin-error" role="alert" data-testid="admin-error">{error}</p>}
        {leads.length === 0 ? <div className="admin-empty" data-testid="admin-empty">No enquiries match these filters yet.</div> : (
          <div className="table-scroll"><table className="leads-table" data-testid="leads-table">
            <thead><tr><th>STUDENT</th><th>CONTACT</th><th>CLASS / EXAM</th><th>FORMAT</th><th>RECEIVED</th><th>STATUS</th></tr></thead>
            <tbody>{leads.map((lead) => {
              const ref = lead.lead_reference;
              const isOpen = expanded === ref;
              return [
                <tr className="lead-row" key={ref} onClick={() => setExpanded(isOpen ? null : ref)} data-testid={`lead-row-${ref}`}>
                  <td><div className="lead-name">{lead.student_name}</div><div className="lead-sub">{ref}</div></td>
                  <td>{lead.mobile}<div className="lead-sub">Prefers {lead.contact_time}</div></td>
                  <td>{lead.current_class}<div className="lead-sub">{lead.target_exam}</div></td>
                  <td>{lead.learning_format}</td>
                  <td>{formatDate(lead.created_at)}</td>
                  <td><select className="status-select" value={lead.status} onClick={(e) => e.stopPropagation()} onChange={(e) => updateStatus(ref, e.target.value)} data-testid={`status-select-${ref}`}>{STATUSES.map((s) => <option key={s}>{s}</option>)}</select></td>
                </tr>,
                isOpen && <tr className="lead-detail" key={`${ref}-detail`}><td colSpan={6}>
                  <div className="lead-detail-grid">
                    <div>
                      <h4>Enquiry details</h4>
                      <p><b>Challenge:</b> {lead.challenge}</p>
                      <p><b>Message:</b> {lead.message || "—"}</p>
                      <p><b>Consent to contact:</b> {lead.consent ? "Yes" : "No"}</p>
                      <a className="text-link" href={`https://wa.me/91${lead.whatsapp}`} target="_blank" rel="noreferrer" data-testid={`wa-${ref}`}><MessageCircle size={14} /> Message student on WhatsApp ↗</a>
                    </div>
                    <div>
                      <h4>Follow-up notes</h4>
                      <textarea rows={4} value={notesDraft[ref] ?? lead.follow_up_notes ?? ""} onChange={(e) => setNotesDraft((current) => ({ ...current, [ref]: e.target.value }))} placeholder="Call outcome, demo timing, next step…" data-testid={`notes-${ref}`} />
                      <button className="admin-btn" onClick={() => saveNotes(ref)} data-testid={`save-notes-${ref}`}>Save notes</button>
                    </div>
                  </div>
                </td></tr>,
              ];
            })}</tbody>
          </table></div>
        )}</>)}
        {tab === "students" && <AdminStudents />}
        {tab === "topics" && <AdminTopics onManageQuestions={manageTopicQuestions} />}
        {tab === "ai" && <AdminAutoQuestions />}
        {tab === "papers" && <AdminPapers />}
        {tab === "homework" && <AdminHomework />}
        {tab === "testbank" && <AdminTestBank />}
        {tab === "reviews" && <AdminReviews />}
        {tab === "mock" && <AdminMockTests />}
        {tab === "questions" && <AdminQuestions topicFilter={questionTopicFilter} setTopicFilter={setQuestionTopicFilter} />}
        {tab === "daily" && <AdminDaily />}
        {tab === "challenges" && <AdminChallenges />}
        {tab === "series" && <AdminSeries />}
        {tab === "products" && <AdminProducts />}
        {tab === "orders" && <AdminOrders />}
        {tab === "bookings" && <AdminBookings />}
        {tab === "cashback" && <AdminCashback />}
        {tab === "analytics" && <AdminAnalytics />}
        {tab === "settings" && <AdminTestSettings />}
      </main>
    </div>
  );
}
